import { undo, redo } from "@codemirror/commands";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { useEditingContext } from "./contexts/editing-context.js";
import { TableSelection, useSelectionContext } from "./contexts/selection-context.js";
import { useTableContext } from "./contexts/table-context.js";
import { useTabularContext } from "./contexts/tabular-context.js";
import { Row } from "./row.js";
import { ColumnSelector } from "./selectors.js";
import { useCodeMirrorViewContext } from "./codemirror-view-context.js";

const MINIMUM_CELL_WIDTH_CHARACTERS = 15;
const MINIMUM_EDITING_CELL_WIDTH_CHARACTERS = 20;
const CELL_WIDTH_BUFFER = 3;

function isCharacterInput(event) {
    return Boolean(event.code) && event.key?.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
}

export function Table() {
    const { selection, setSelection } = useSelectionContext();
    const { cellData, cancelEditing, startEditing, commitCellData, clearCells, updateCellData } = useEditingContext();
    const { table: tableData } = useTableContext();
    const { columnWidthModalShown } = useTabularContext();
    const tableRef = useRef(null);
    const view = useCodeMirrorViewContext();

    const { columns: cellWidths, tableWidth } = useMemo(() => {
        const columns = Array.from({ length: tableData.columns.length }, () => MINIMUM_CELL_WIDTH_CHARACTERS);
        for (let row = 0; row < tableData.rows.length; row += 1) {
            for (let i = 0; i < tableData.columns.length; i += tableData.getCell(row, i).multiColumn?.columnSpan ?? 1) {
                const span = tableData.getCell(row, i).multiColumn?.columnSpan ?? 1;
                let contentLength = tableData.getCell(row, i).content.length + CELL_WIDTH_BUFFER;
                if (cellData?.rowIndex === row && cellData?.cellIndex === i) {
                    contentLength = Math.max(
                        contentLength,
                        Math.min(cellData.content.length + CELL_WIDTH_BUFFER, MINIMUM_EDITING_CELL_WIDTH_CHARACTERS),
                    );
                }
                for (let j = 0; j < span; j += 1) {
                    columns[i + j] = Math.max(columns[i + j], contentLength / span);
                }
            }
        }
        const total = columns.reduce((a, b) => a + b, 0);
        for (let i = 0; i < columns.length; i += 1) {
            columns[i] = Math.log2(columns[i]);
        }
        const totalLog = columns.reduce((a, b) => a + b, 0);
        for (let i = 0; i < columns.length; i += 1) {
            columns[i] = Math.round((columns[i] / totalLog) * 100);
        }
        return { columns, tableWidth: total };
    }, [cellData?.cellIndex, cellData?.content.length, cellData?.rowIndex, tableData]);

    const navigation = useMemo(
        () => ({
            ArrowRight: { run: () => selection.moveRight(tableData), shift: () => selection.extendRight(tableData) },
            ArrowLeft: { run: () => selection.moveLeft(tableData), shift: () => selection.extendLeft(tableData) },
            ArrowUp: { run: () => selection.moveUp(tableData), shift: () => selection.extendUp(tableData) },
            ArrowDown: { run: () => selection.moveDown(tableData), shift: () => selection.extendDown(tableData) },
            Tab: {
                run: () => selection.moveNext(tableData),
                shift: () => selection.movePrevious(tableData),
                canExitEditing: true,
            },
        }),
        [selection, tableData],
    );

    const onKeyDown = useCallback(
        (event) => {
            if (view.state.readOnly) {
                return;
            }
            const commandKey = /Mac|iPhone|iPad/.test(window.navigator.platform) ? event.metaKey : event.ctrlKey;
            if (event.code === "Enter" && !event.shiftKey) {
                event.preventDefault();
                event.stopPropagation();
                if (!selection) {
                    return;
                }
                if (cellData) {
                    commitCellData();
                } else {
                    const initialContent = tableData.getCell(selection.to.row, selection.to.cell).content;
                    startEditing(selection.to.row, selection.to.cell, initialContent);
                }
                setSelection(new TableSelection(selection.to, selection.to).explode(tableData));
                return;
            }
            if (event.code === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                if (!cellData) {
                    setSelection(null);
                    view.focus();
                } else {
                    cancelEditing();
                }
                return;
            }
            if (event.code === "Delete" || event.code === "Backspace") {
                if (cellData || !selection) {
                    return;
                }
                event.preventDefault();
                event.stopPropagation();
                clearCells(selection);
                view.requestMeasure();
                setTimeout(() => {
                    if (tableRef.current) {
                        const { minY } = selection.normalized();
                        const row = tableRef.current.querySelectorAll("tbody tr")[minY];
                        row?.scrollIntoView?.({ block: "center" });
                    }
                }, 0);
                return;
            }
            if (Object.prototype.hasOwnProperty.call(navigation, event.code)) {
                const { run, shift, canExitEditing } = navigation[event.code];
                if (cellData && !canExitEditing) {
                    return;
                }
                event.preventDefault();
                if (!selection) {
                    setSelection(new TableSelection({ row: 0, cell: 0 }, { row: 0, cell: 0 }).explode(tableData));
                    return;
                }
                const nextSelection = event.shiftKey ? shift() : run();
                if (cellData && canExitEditing) {
                    commitCellData();
                }
                setSelection(nextSelection);
                return;
            }
            if (isCharacterInput(event) && !cellData) {
                event.preventDefault();
                event.stopPropagation();
                if (!selection) {
                    return;
                }
                startEditing(selection.to.row, selection.to.cell);
                updateCellData(event.key);
                setSelection(new TableSelection(selection.to, selection.to));
                return;
            }
            if (!cellData && event.key === "z" && !event.shiftKey && commandKey) {
                event.preventDefault();
                undo(view);
                return;
            }
            if (!cellData && (event.key === "y" || (event.key === "Z" && event.shiftKey && commandKey))) {
                event.preventDefault();
                redo(view);
            }
        },
        [
            cancelEditing,
            cellData,
            clearCells,
            commitCellData,
            navigation,
            selection,
            setSelection,
            startEditing,
            tableData,
            updateCellData,
            view,
        ],
    );

    useEffect(() => {
        const onPaste = (event) => {
            if (view.state.readOnly || cellData || !selection || columnWidthModalShown) {
                return false;
            }
            event.preventDefault();
            const changes = [];
            const data = event.clipboardData?.getData("text/plain");
            if (data) {
                const cells = data.split("\n").map((row) => row.split("\t"));
                const { minY, minX } = selection.normalized();
                for (let row = 0; row < cells.length; row += 1) {
                    const rowIndex = minY + row;
                    if (rowIndex >= tableData.rows.length) {
                        break;
                    }
                    const cellStart = tableData.getCellIndex(rowIndex, minX);
                    for (let column = 0; column < cells[row].length; column += 1) {
                        const cellIndex = cellStart + column;
                        if (cellIndex >= tableData.rows[rowIndex].cells.length) {
                            break;
                        }
                        const cell = tableData.rows[rowIndex].cells[cellIndex];
                        changes.push({ from: cell.from, to: cell.to, insert: cells[row][column] });
                    }
                }
            }
            view.dispatch({ changes });
            return undefined;
        };

        const onCopy = (event) => {
            if (cellData || !selection || columnWidthModalShown) {
                return false;
            }
            event.preventDefault();
            const { minY, maxY } = selection.normalized();
            const cells = Array.from({ length: maxY - minY + 1 }, () => []);
            tableData.iterateSelection(selection, (cell, row) => {
                cells[row - minY].push(cell.content);
            });
            const content = cells.map((row) => row.join("\t")).join("\n");
            event.clipboardData?.setData("text/plain", content);
            return undefined;
        };

        window.addEventListener("paste", onPaste);
        window.addEventListener("copy", onCopy);
        return () => {
            window.removeEventListener("paste", onPaste);
            window.removeEventListener("copy", onCopy);
        };
    }, [cellData, columnWidthModalShown, selection, tableData, view]);

    return (
        <table
            className="table-generator-table"
            onKeyDown={onKeyDown}
            tabIndex={-1}
            ref={tableRef}
            style={{ width: `min(${tableWidth}ch, 95%)` }}
        >
            <colgroup>
                <col width="20" />
                {tableData.columns.map((_, index) => (
                    <col key={index} width={`${cellWidths[index]}%`} />
                ))}
            </colgroup>
            <thead>
                <tr>
                    <td />
                    {tableData.columns.map((_, columnIndex) => (
                        <ColumnSelector index={columnIndex} key={columnIndex} />
                    ))}
                </tr>
            </thead>
            <tbody>
                {tableData.rows.map((row, rowIndex) => (
                    <Row row={row} rowIndex={rowIndex} key={rowIndex} columnSpecifications={tableData.columns} />
                ))}
            </tbody>
        </table>
    );
}
